import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import '../styles/Dashboard.css';
import SummarySection from './SummarySection';
import API_BASE_URL from '../../config/api';


const Dashboard = ({ fileId }) => {
  const [laps, setLaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds < 0) return '00:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const formatPace = useCallback((paceInMinutes) => {
    if (!paceInMinutes || paceInMinutes <= 0) return '00:00';
    const minutes = Math.floor(paceInMinutes);
    const seconds = Math.round((paceInMinutes - minutes) * 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, []);

  const calculatePace = useCallback((speed) => {
    if (!speed || speed <= 0) return '00:00';
    return formatPace(60 / speed);
  }, [formatPace]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_BASE_URL}/api/fit/parsed-data/${fileId}`);
        const data = res.data;

        if (!Array.isArray(data.laps)) {
          throw new Error('❌ Estructura inválida: laps no disponible o no es un array');
        }

        let accumulatedTime = 0;
        const lapsData = data.laps.map((lap, index) => {
          const totalElapsedTime = lap.total_elapsed_time || 0;
          accumulatedTime += totalElapsedTime;

          const avgPace = 60 / ((lap.enhanced_avg_speed || 0) * 3.6);

          return {
            vuelta: index + 1,
            duracion: formatTime(totalElapsedTime),
            tiempoAcumulado: formatTime(accumulatedTime),
            distancia: ((lap.total_distance || 0) / 1000).toFixed(2),
            ritmoPromedio: calculatePace((lap.enhanced_avg_speed || 0) * 3.6),
            ritmoMaximo: calculatePace((lap.enhanced_max_speed || 0) * 3.6),
            gapMedio: avgPace, // se usará para calcular el promedio
            calorias: lap.total_calories || 0,
            ascenso: lap.total_ascent || 0,
            descenso: lap.total_descent || 0,
            frecuenciaCardiacaPromedio: lap.avg_heart_rate || 0,
            frecuenciaCardiacaMaxima: lap.max_heart_rate || 0,
            velocidadPromedio: ((lap.enhanced_avg_speed || 0) * 3.6).toFixed(2),
            velocidadMaxima: ((lap.enhanced_max_speed || 0) * 3.6).toFixed(2),
            cadenciaPromedio: lap.avg_cadence || 0,
            temperaturaPromedio: lap.avg_temperature || 0,
            temperaturaMaxima: lap.max_temperature || 0
          };
        });

        setLaps(lapsData);
      } catch (err) {
        console.error('Error al cargar los datos:', err);
        setError(err.message || 'Error desconocido');
        setLaps([]);
      } finally {
        setLoading(false);
      }
    };

    if (fileId) {
      fetchData();
    }
  }, [fileId, calculatePace, formatPace]);

  const totalsAndAverages = useMemo(() => {
    if (laps.length === 0) return {};
    let totalDuration = 0, totalDistance = 0, totalCalories = 0, totalAscent = 0, totalDescent = 0;
    let totalAvgHeartRate = 0, totalMaxHeartRate = 0, totalAvgSpeed = 0, totalMaxSpeed = 0;
    let totalCadence = 0, totalTemp = 0, totalMaxTemp = 0, totalRitmoMaximo = 0;
    let totalGap = 0;

    laps.forEach(lap => {
      const [hh, mm, ss] = lap.duracion.split(':').map(Number);
      totalDuration += hh * 3600 + mm * 60 + ss;
      totalDistance += parseFloat(lap.distancia);
      totalCalories += parseFloat(lap.calorias);
      totalAscent += parseFloat(lap.ascenso);
      totalDescent += parseFloat(lap.descenso);
      totalAvgHeartRate += parseFloat(lap.frecuenciaCardiacaPromedio);
      totalMaxHeartRate += parseFloat(lap.frecuenciaCardiacaMaxima);
      totalAvgSpeed += parseFloat(lap.velocidadPromedio);
      totalMaxSpeed += parseFloat(lap.velocidadMaxima);
      totalCadence += parseFloat(lap.cadenciaPromedio);
      totalTemp += parseFloat(lap.temperaturaPromedio);
      totalMaxTemp += parseFloat(lap.temperaturaMaxima);
      totalRitmoMaximo = Math.max(totalRitmoMaximo, parseFloat(lap.ritmoMaximo.replace(':', '.')));
      totalGap += parseFloat(lap.gapMedio);
    });

    const totalLaps = laps.length;

    return {
      totalLaps,
      totalDuration: formatTime(totalDuration),
      totalDistance: totalDistance.toFixed(2),
      totalCalories: totalCalories.toFixed(2),
      totalAscent: totalAscent.toFixed(2),
      totalDescent: totalDescent.toFixed(2),
      averagePace: formatPace(totalAvgSpeed / totalLaps),
      averageHeartRate: (totalAvgHeartRate / totalLaps).toFixed(2),
      averageMaxHeartRate: (totalMaxHeartRate / totalLaps).toFixed(2),
      averageSpeed: (totalAvgSpeed / totalLaps).toFixed(2),
      averageMaxSpeed: (totalMaxSpeed / totalLaps).toFixed(2),
      averageCadence: (totalCadence / totalLaps).toFixed(2),
      averageTemp: (totalTemp / totalLaps).toFixed(2),
      averageMaxTemp: (totalMaxTemp / totalLaps).toFixed(2),
      averageRitmoMaximo: formatPace(totalRitmoMaximo),
      gapMedioPromedio: formatPace(totalGap / totalLaps) // ✅ se muestra formateado en min/km
    };
  }, [laps, formatPace]);

  return (
    <div className="dashboard-container">
      {loading ? (
        <p>Cargando datos...</p>
      ) : error ? (
        <p>Error: {error}</p>
      ) : (
        <>
          <SummarySection totalsAndAverages={totalsAndAverages} />
        </>
      )}
    </div>
  );
};

export default Dashboard;
